import Button from "@/components/Button";
import TextField from "@/components/TextField";
import { useMutation } from "@apollo/client";
import { useForm } from "react-hook-form";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { z } from "zod/v3";
import { PATCH_FACILITY_MUTATION } from "@/features/facility/queries";
import { EditProps } from "./types";
import { NullableStringSchema } from "./validation";

const PersonnelSchema = z.object({
  generalManagerName: NullableStringSchema,
  managementGroup: NullableStringSchema
});

type FieldValues = z.infer<typeof PersonnelSchema>;

interface PersonnelEditFormProps extends EditProps {
  generalManagerName?: string | null;
  managementGroup?: string | null;
}

const defaultErrorMessage = "Failed to update personnel information";

export default function PersonnelEditForm({
  facilityId,
  generalManagerName: initialGeneralManagerName,
  managementGroup: initialManagementGroup,
  onCancel,
  onSuccess,
  onError
}: PersonnelEditFormProps) {
  const form = useForm<FieldValues>({
    defaultValues: {
      generalManagerName: initialGeneralManagerName || "",
      managementGroup: initialManagementGroup || ""
    },
    resolver: zodResolver(PersonnelSchema),
    mode: "onChange"
  });

  const [patchFacility, { loading }] = useMutation(PATCH_FACILITY_MUTATION);

  async function onSubmit(values: FieldValues) {
    try {
      const input = {
        id: facilityId,
        ...values
      };

      const { data } = await patchFacility({
        variables: {
          input
        }
      });

      const {
        patchFacility: { success, facility, message }
      } = data;

      if (success) {
        return onSuccess(facility);
      }
      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="personnel-edit-form">
      <TextField
        className="w-full"
        label="General Manager"
        value={form.watch("generalManagerName") || ""}
        {...form.register("generalManagerName")}
        disabled={loading}
        error={!!form.formState.errors.generalManagerName}
        errorText={form.formState.errors.generalManagerName?.message}
      />
      <TextField
        className="w-full"
        label="Management Group"
        value={form.watch("managementGroup") || ""}
        {...form.register("managementGroup")}
        disabled={loading}
        error={!!form.formState.errors.managementGroup}
        errorText={form.formState.errors.managementGroup?.message}
      />
      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={loading} onClick={form.handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}
